import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, fileUrl } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { useDialog } from "../../lib/dialog";
import AdminLayout from "../../components/app/AdminLayout";
import { StatusBadge, RoleAssignedBadge, RoleRejectedBadge } from "../../components/app/StatusBadge";
import { Loader } from "./Dashboard";
import { Search, Eye, Download, ChevronDown, ChevronUp, LayoutGrid, List, User, FileCheck2, FileClock, FileText, Loader2, X } from "lucide-react";

const VIEW_KEY = "ssm_applicants_view";
const FILTERS_KEY = "ssm_applicants_filters";
const PAGE_SIZE = 24;
const DEFAULT_FILTERS = { status: "all", gender: "all", experience: "all", search: "", sort: "-submitted_at", min_availability: "" };

const loadSavedFilters = () => {
  if (typeof window === "undefined") return { ...DEFAULT_FILTERS };
  try {
    const raw = window.sessionStorage.getItem(FILTERS_KEY);
    return raw ? { ...DEFAULT_FILTERS, ...JSON.parse(raw) } : { ...DEFAULT_FILTERS };
  } catch (e) { return { ...DEFAULT_FILTERS }; }
};

export default function Applicants() {
  const { formatDate } = useSettings();
  const dialog = useDialog();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState(loadSavedFilters);
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "list";
    return window.localStorage.getItem(VIEW_KEY) || "list";
  });
  const navigate = useNavigate();
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const itemsRef = useRef(null);
  const [newIds, setNewIds] = useState(() => new Set());
  const hasMore = items !== null && items.length < total;

  const setViewPersist = (v) => {
    setView(v);
    try { window.localStorage.setItem(VIEW_KEY, v); } catch (e) { console.warn("Could not persist view preference:", e); }
  };

  const buildParams = (skip) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
    params.set("limit", String(PAGE_SIZE));
    params.set("skip", String(skip));
    return params;
  };

  // (Re)load the first page — called on mount and whenever filters change.
  const loadFirst = () => {
    loadingRef.current = true;
    skipRef.current = 0;
    setItems(null);
    adminApi.get(`/admin/applicants?${buildParams(0).toString()}`).then(({ data }) => {
      setItems(data.items);
      setTotal(data.total);
      skipRef.current = data.items.length;
    }).finally(() => { loadingRef.current = false; });
  };

  // Infinite scroll — append the next page.
  const loadMore = async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const { data } = await adminApi.get(`/admin/applicants?${buildParams(skipRef.current).toString()}`);
      setItems((prev) => [...(prev || []), ...data.items]);
      setTotal(data.total);
      skipRef.current += data.items.length;
    } finally { loadingRef.current = false; setLoadingMore(false); }
  };

  useEffect(() => { loadFirst(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items)); }, []);

  // Persist filters across navigation (e.g. open a profile and click Back).
  useEffect(() => {
    try { window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch (e) { /* ignore */ }
  }, [filters]);

  const filtersDirty = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const clearFilters = () => setFilters({ ...DEFAULT_FILTERS });

  // Observe the sentinel to auto-load more as it scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep a live ref of the current list for the background poller.
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Background polling — new applications appear in the list automatically (no refresh).
  const pollNew = async () => {
    if (loadingRef.current || (typeof document !== "undefined" && document.hidden)) return;
    const cur = itemsRef.current;
    if (cur === null) return;
    try {
      const { data } = await adminApi.get(`/admin/applicants?${buildParams(0).toString()}`);
      const existing = new Set(cur.map((x) => x.id));
      const fresh = data.items.filter((x) => !existing.has(x.id));
      setTotal(data.total);
      if (fresh.length) {
        skipRef.current += fresh.length;
        const ids = fresh.map((f) => f.id);
        setItems((prev) => (prev ? [...fresh, ...prev] : prev));
        setNewIds((s) => new Set([...s, ...ids]));
        // Fade the "new" highlight after a few seconds.
        setTimeout(() => setNewIds((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; }), 6000);
      }
    } catch (e) { /* ignore transient poll errors */ }
  };

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) pollNew(); };
    const id = setInterval(pollNew, 25000);
    window.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { clearInterval(id); window.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAll = () => setSelected((items || []).length === selected.length ? [] : (items || []).map((x) => x.id));

  const runBulk = async (action, opts = {}) => {
    if (selected.length === 0) return;
    await adminApi.post("/admin/applicants/bulk", { applicant_ids: selected, action, ...opts });
    setSelected([]); loadFirst();
  };

  const sendContracts = async () => {
    if (selected.length === 0) return;
    if (!await dialog.confirm({ title: "Send contracts?", description: `Email a contract link to the selected applicants. Only those at 'Offered a Role' or above will receive one.`, confirmLabel: "Send Contracts" })) return;
    const { data } = await adminApi.post("/admin/applicants/bulk-send-contract", { applicant_ids: selected });
    setSelected([]); loadFirst();
    await dialog.alert({ title: "Contracts sent", description: `${data.sent} contract${data.sent === 1 ? "" : "s"} sent${data.skipped ? `, ${data.skipped} skipped (not eligible)` : ""}.` });
  };

  const exportCsv = () => {
    if (!items) return;
    const rows = [["Name","Email","Phone","Gender","Experience","Status","Date Submitted","Availability Count"]];
    items.forEach((a) => rows.push([a.full_name, a.email, a.phone, a.gender, a.experience_level, a.status, a.submitted_at, (a.available_dates||[]).length]));
    const csv = rows.map((r) => r.map((c) => `"${String(c||"").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `applicants-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const ageFor = (dob) => {
    if (!dob) return "—";
    try { const d = new Date(dob); const diff = Date.now() - d.getTime(); return Math.floor(diff / (1000*60*60*24*365.25)); }
    catch { return "—"; }
  };

  return (
    <AdminLayout
      title="Applicants"
      subtitle="Manage and review all scare actor applications"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex border border-[#262626]" data-testid="view-toggle">
            <button
              onClick={() => setViewPersist("list")}
              data-testid="view-list-btn"
              title="List view"
              className={`p-2 transition-colors ${view === "list" ? "bg-blood text-white" : "text-[#A19D94] hover:text-blood"}`}
            ><List size={14} /></button>
            <button
              onClick={() => setViewPersist("grid")}
              data-testid="view-grid-btn"
              title="Grid view"
              className={`p-2 transition-colors ${view === "grid" ? "bg-blood text-white" : "text-[#A19D94] hover:text-blood"}`}
            ><LayoutGrid size={14} /></button>
          </div>
          <button onClick={exportCsv} data-testid="export-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
            <Download size={14} /> Export
          </button>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
          <input
            data-testid="search-input"
            value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search applicants…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#0A0A0A] border border-[#262626] focus:outline-none focus:border-blood"
          />
        </div>
        <FilterSelect data-testid="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={[["all","All Status"], ...statuses.map(s=>[s.key,s.label])]} />
        <FilterSelect data-testid="filter-gender" value={filters.gender} onChange={(v) => setFilters((f) => ({ ...f, gender: v }))} options={[["all","All Gender"], ["Male","Male"], ["Female","Female"], ["Non-binary","Non-binary"], ["Other","Other"]]} />
        <FilterSelect data-testid="filter-experience" value={filters.experience} onChange={(v) => setFilters((f) => ({ ...f, experience: v }))} options={[["all","All Experience"], ["No acting experience","No experience"], ["I have acting experience but not as a scare actor","Some acting"], ["I have scare acting experience at another attraction","Scare exp (other)"], ["I have scare acting experience at Stourbridge Scare Maze","Scare exp (SSM)"]]} />
        <FilterSelect data-testid="filter-sort" value={filters.sort} onChange={(v) => setFilters((f) => ({ ...f, sort: v }))} options={[["-submitted_at","Newest first"], ["submitted_at","Oldest first"], ["full_name","Name A–Z"], ["-full_name","Name Z–A"], ["-audition_score","Highest score"], ["audition_score","Lowest score"], ["-availability","Most available"], ["availability","Least available"], ["status","Status"]]} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <label className="flex items-center gap-2 text-[#A19D94]">
          <span className="label-mini">Min. availability</span>
          <input type="number" min={0} data-testid="filter-min-availability" value={filters.min_availability}
            onChange={(e) => setFilters((f) => ({ ...f, min_availability: e.target.value }))}
            placeholder="any" className="w-20 bg-[#0A0A0A] border border-[#262626] px-2 py-1.5 text-sm focus:outline-none focus:border-blood" />
          <span className="text-xs text-[#6B6862]">dates</span>
        </label>
        {filters.min_availability && (
          <button onClick={() => setFilters((f) => ({ ...f, min_availability: "" }))} data-testid="reset-min-availability" className="text-xs uppercase tracking-wider text-[#6B6862] hover:text-blood">Reset</button>
        )}
        {filtersDirty && (
          <button onClick={clearFilters} data-testid="clear-filters-btn" className="flex items-center gap-1.5 ml-auto px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
            <X size={13} /> Clear Filters
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 border border-blood/40 bg-blood/5">
          <span className="text-sm text-blood font-mono">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <select onChange={(e)=>setBulkStatus(e.target.value)} value={bulkStatus} className="text-xs bg-[#0A0A0A] border border-[#262626] px-2 py-1.5">
              <option value="">Change status…</option>
              {statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <button onClick={()=>bulkStatus && runBulk("update_status", { status: bulkStatus })} disabled={!bulkStatus} className="px-3 py-1.5 text-xs bg-blood text-white disabled:opacity-40">Apply</button>
            <button onClick={()=>runBulk("retain_talent_pool")} className="px-3 py-1.5 text-xs border border-[#262626] hover:border-blood">→ Talent Pool</button>
            <button onClick={sendContracts} data-testid="bulk-send-contracts" className="px-3 py-1.5 text-xs border border-[#262626] hover:border-blood inline-flex items-center gap-1.5"><FileText size={12} /> Send Contracts</button>
            <button onClick={()=>setSelected([])} className="px-3 py-1.5 text-xs text-[#A19D94] hover:text-[#F2EBE1]">Clear</button>
          </div>
        </div>
      )}

      {items === null ? <Loader /> : view === "grid" ? (
        <GridView items={items} statuses={statuses} ageFor={ageFor} navigate={navigate} total={total} newIds={newIds} />
      ) : (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm" data-testid="applicants-table">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-left">
                <th className="px-3 py-3 w-8"><input type="checkbox" checked={selected.length === items.length && items.length > 0} onChange={selectAll} className="accent-[#D91616]" /></th>
                <SortHeader label="Name" field="full_name" filters={filters} setFilters={setFilters} />
                <SortHeader label="Date Submitted" field="submitted_at" filters={filters} setFilters={setFilters} />
                <th className="px-3 py-2 label-mini">Gender</th>
                <th className="px-3 py-2 label-mini">Age</th>
                <th className="px-3 py-2 label-mini">Experience</th>
                <SortHeader label="Availability" field="availability" filters={filters} setFilters={setFilters} />
                <SortHeader label="Score" field="audition_score" filters={filters} setFilters={setFilters} />
                <th className="px-3 py-2 label-mini">Status</th>
                <th className="px-3 py-2 label-mini text-center">Contract</th>
                <th className="px-3 py-2 label-mini">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={11} className="py-12 text-center text-[#6B6862]">No applicants match your filters.</td></tr>
              ) : items.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/admin/applicants/${a.id}`)}
                  data-testid={`row-${a.id}`}
                  className={`border-b border-[#1a1a1a] hover:bg-[#101010] transition-colors cursor-pointer ${newIds.has(a.id) ? "bg-blood/[0.07] border-l-2 border-l-blood" : ""}`}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleSelect(a.id)} className="accent-[#D91616]" />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {a.headshot_file_id ? (
                        <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt="" loading="lazy" decoding="async" className="w-9 h-9 object-cover border border-[#262626] bg-[#1a1a1a]" />
                      ) : (
                        <div className="w-9 h-9 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center font-display text-blood text-sm">{a.full_name?.[0]}</div>
                      )}
                      <span className="text-[#F2EBE1] group-hover:text-blood">{a.full_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-[#A19D94]">{formatDate(a.submitted_at)}</td>
                  <td className="px-3 py-3 text-[#A19D94]">{a.gender || "—"}</td>
                  <td className="px-3 py-3 font-mono text-[#A19D94]">{ageFor(a.date_of_birth)}</td>
                  <td className="px-3 py-3 text-xs text-[#A19D94] max-w-[200px] truncate" title={a.experience_level}>{shortenExp(a.experience_level)}</td>
                  <td className="px-3 py-3 font-mono text-xs text-[#A19D94]">{(a.available_dates||[]).length}</td>
                  <td className="px-3 py-3" data-testid={`score-cell-${a.id}`}><ScoreBadge score={a.audition_score} judges={a.audition_score_judges} /></td>
                  <td className="px-3 py-3">{a.role_declined ? <RoleRejectedBadge /> : a.assigned_role_count > 0 ? <RoleAssignedBadge /> : <StatusBadge status={a.status} statuses={statuses} />}</td>
                  <td className="px-3 py-3 text-center" data-testid={`contract-cell-${a.id}`}><ContractIcon a={a} /></td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/admin/applicants/${a.id}`} data-testid={`view-${a.id}`} className="text-[#A19D94] hover:text-blood inline-flex"><Eye size={16} /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#1a1a1a] text-xs text-[#A19D94] font-mono">
            <span>Showing {items.length} of {total} results</span>
          </div>
        </div>
      )}

      {items !== null && (
        <div ref={sentinelRef} data-testid="applicants-infinite-sentinel" className="py-6 flex justify-center">
          {loadingMore ? (
            <span className="flex items-center gap-2 text-xs text-[#6B6862] uppercase tracking-wider"><Loader2 size={14} className="animate-spin" /> Loading more…</span>
          ) : hasMore ? (
            <button onClick={loadMore} data-testid="applicants-load-more" className="text-xs uppercase tracking-wider text-[#A19D94] hover:text-blood border border-[#262626] hover:border-blood px-4 py-2">Load more</button>
          ) : items.length > 0 ? (
            <span className="text-xs text-[#3a3835] uppercase tracking-wider">All {total} applicant{total === 1 ? "" : "s"} loaded</span>
          ) : null}
        </div>
      )}
    </AdminLayout>
  );
}

function GridView({ items, statuses, ageFor, navigate, total, newIds }) {
  const expBlurb = (e) => {
    if (!e) return null;
    if (e.startsWith("No acting")) return "No experience";
    if (e.includes("not as a scare")) return "Acting, not scare";
    if (e.includes("another attraction")) return "Scare exp (other)";
    if (e.includes("Stourbridge")) return "Scare exp (SSM)";
    return e.slice(0, 30);
  };

  if (items.length === 0) {
    return <div className="border border-[#1a1a1a] bg-[#0A0A0A] py-16 text-center text-[#6B6862]">No applicants match your filters.</div>;
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {items.map((a) => {
          const status = statuses.find((s) => s.key === a.status);
          return (
            <button
              key={a.id}
              onClick={() => navigate(`/admin/applicants/${a.id}`)}
              data-testid={`card-${a.id}`}
              className={`group text-left border bg-[#0A0A0A] hover:border-blood transition-colors overflow-hidden ${newIds && newIds.has(a.id) ? "border-blood ring-1 ring-blood" : "border-[#1a1a1a]"}`}
            >
              <div className="relative aspect-square bg-[#1a1a1a] overflow-hidden">
                {a.headshot_file_id ? (
                  <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={56} className="text-[#3a3835]" strokeWidth={1} />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent pointer-events-none" />
                <div className="absolute top-2 left-2">
                  {a.role_declined ? (
                    <span data-testid="status-role-rejected" className="inline-flex items-center gap-1.5 px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-semibold bg-black/70 backdrop-blur border border-blood text-blood">
                      <span className="w-1.5 h-1.5 rounded-full bg-blood" />
                      Role Rejected
                    </span>
                  ) : a.assigned_role_count > 0 ? (
                    <span data-testid="status-role-assigned" className="inline-flex items-center gap-1.5 px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-semibold bg-black/70 backdrop-blur border border-[#10B981] text-[#10B981]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                      Role Assigned
                    </span>
                  ) : status && (
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-semibold bg-black/70 backdrop-blur border"
                      style={{ borderColor: status.color, color: status.color }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
                      {status.label}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3">
                <div className="font-display text-base uppercase tracking-tight truncate" title={a.full_name}>{a.full_name}</div>
                <div className="text-[10px] text-[#A19D94] mt-1 font-mono uppercase tracking-wider flex items-center gap-1.5 flex-wrap">
                  <span>{ageFor(a.date_of_birth)}{a.date_of_birth ? "y" : ""}</span>
                  <span className="text-[#3a3835]">·</span>
                  <span>{a.gender || "—"}</span>
                </div>
                <div className="text-[10px] text-[#6B6862] mt-1 truncate" title={a.experience_level}>{expBlurb(a.experience_level) || "—"}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="px-1 py-3 text-xs text-[#A19D94] font-mono">Showing {items.length} of {total} results</div>
    </>
  );
}

const shortenExp = (e) => {
  if (!e) return "—";
  if (e.startsWith("No acting")) return "No experience";
  if (e.includes("not as a scare")) return "Acting, not scare";
  if (e.includes("another attraction")) return "Scare exp (other)";
  if (e.includes("Stourbridge")) return "Scare exp (SSM)";
  return e.slice(0,30);
};

const ContractIcon = ({ a }) => {
  if (a.contract_accepted_at) return <span title="Contract accepted" className="inline-flex text-[#10B981]"><FileCheck2 size={16} /></span>;
  if (a.contract_sent_at) return <span title="Contract sent — awaiting acceptance" className="inline-flex text-[#F59E0B]"><FileClock size={16} /></span>;
  return <span className="text-[#3a3835]">—</span>;
};

const FilterSelect = ({ value, onChange, options, ...rest }) => (
  <select {...rest} value={value} onChange={(e) => onChange(e.target.value)} className="text-sm bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-blood">
    {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const SortHeader = ({ label, field, filters, setFilters }) => {
  const active = filters.sort.replace("-", "") === field;
  const desc = filters.sort.startsWith("-");
  const toggle = () => setFilters((f) => ({ ...f, sort: (active && !desc) ? `-${field}` : field }));
  return (
    <th className="px-3 py-2 label-mini cursor-pointer hover:text-blood" onClick={toggle}>
      <span className="inline-flex items-center gap-1">{label}{active && (desc ? <ChevronDown size={10} /> : <ChevronUp size={10} />)}</span>
    </th>
  );
};


const ScoreBadge = ({ score, judges }) => {
  if (score == null) return <span className="text-[#4a4843] text-xs">—</span>;
  const color = score >= 7 ? "#22C55E" : score >= 5 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-flex items-center gap-1.5" title={`Overall audition average${judges ? ` · ${judges} judge${judges > 1 ? "s" : ""}` : ""}`}>
      <span className="font-mono text-sm font-bold" style={{ color }}>{Number(score).toFixed(1)}</span>
      <span className="text-[9px] text-[#6B6862]">/10</span>
    </span>
  );
};
